import { Badge, Button, Card, CardHeader, EmptyState, formatDate, useToast } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { addException, deleteTemplate, listOccurrences, listTemplates, type ClassOccurrence } from "../../api/classes";
import { addDays } from "../../lib/dates";
import { useServices } from "../../app/services";
import { Confirm, Failure, Field, FormError, messageOf, RowsSkeleton, styles as c, useScope, useStaffNames } from "./common";
import { TemplateForm } from "./TemplateForm";
import { slotSummary } from "./TemplatesView";

/** `/classes/templates/:id`. There is no get-by-id route, so the template is found in the list; an id from another business is simply not there. */
export function TemplatePage() {
  const { id = "" } = useParams();
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const { nameOf, list } = useStaffNames();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exOccurrence, setExOccurrence] = useState("");
  const [exStatus, setExStatus] = useState<"cancelled" | "scheduled">("cancelled");
  const [exTrainer, setExTrainer] = useState("");
  // Every branch, unlike the filtered Templates tab, so it needs its own cache key.
  const q = useQuery({ queryKey: ["classes", "templates", "all-branches", ...sc.key], queryFn: () => listTemplates(client, {}).then((r) => r.templates), retry: false });
  const t = q.data?.find((x) => x.id === id) ?? null;
  const upcoming = useQuery({
    queryKey: ["classes", "template-upcoming", ...sc.key, id],
    queryFn: () => listOccurrences(client, { from: sc.date, to: addDays(sc.date, 28), templateId: id }).then((r) => r.occurrences),
    enabled: t !== null, retry: false,
  });
  const canEdit = sc.ctx.has("classes.template_edit");
  const canDelete = sc.ctx.has("classes.template_delete");
  const done = () => void qc.invalidateQueries({ queryKey: ["classes"] });

  const remove = useMutation({ mutationFn: () => deleteTemplate(client, id), onSuccess: () => { done(); toast.show("Template deleted"); nav("/classes?tab=templates"); }, onError: (e) => setError(messageOf(e)) });
  const exception = useMutation({
    mutationFn: (o: { date: string; start: string; status: "cancelled" | "scheduled"; trainerId?: string }) => addException(client, id, o),
    onSuccess: () => { setError(null); done(); toast.show("Exception saved"); },
    onError: (e) => setError(messageOf(e)),
  });

  if (q.isPending) return <Card aria-label="Template"><RowsSkeleton rows={4} /></Card>;
  if (q.error) return <Failure error={q.error} title="Could not load the template" onRetry={() => void q.refetch()} />;
  if (!t) return <Card><EmptyState icon="alert" title="Class template not found" description="It may have been deleted, or it belongs to another gym." action={<Button as={Link} to="/classes?tab=templates">Back to templates</Button>} /></Card>;

  const pick: ClassOccurrence | undefined = upcoming.data?.find((o) => o.id === exOccurrence);
  return (
    <div className={c.page}>
      <div className={c.head}>
        <div><h1>{t.name}</h1><p>{nameOf(t.trainerId, t.trainerName)} · {slotSummary(t)}</p></div>
        <div className={c.tools}>
          <Button as={Link} to="/classes?tab=templates">All templates</Button>
          {canEdit ? <Button variant="primary" onClick={() => setEditing(true)}>Edit template</Button> : null}
          {canDelete ? <Confirm label="Delete template" question="Delete this template? Classes people already booked stay on the calendar." busy={remove.isPending} onConfirm={() => remove.mutate()} /> : null}
        </div>
      </div>
      <FormError message={error} />
      <Card aria-label="Template details">
        <CardHeader title="Details" />
        <dl className={c.pad}>
          <div className={c.row}><dt className={c.note}>Capacity</dt><dd>{t.capacity}</dd></div>
          <div className={c.row}><dt className={c.note}>Length</dt><dd>{t.durationMinutes} minutes</dd></div>
          <div className={c.row}><dt className={c.note}>Runs</dt><dd>{formatDate(t.effectiveFrom, sc.ctx.locale, "full")}{t.effectiveTo ? ` to ${formatDate(t.effectiveTo, sc.ctx.locale, "full")}` : " onwards"}</dd></div>
        </dl>
      </Card>
      {canEdit ? (
        <Card aria-label="Exceptions">
          <CardHeader title="Cancel or substitute one class" subtitle="Pick a date this template runs on." />
          <div className={`${c.pad} ${c.stack}`}>
            <Field label="Date" htmlFor="ex-date">
              <select id="ex-date" className={c.input} value={exOccurrence} onChange={(e) => setExOccurrence(e.target.value)}>
                <option value="">Choose a date</option>
                {(upcoming.data ?? []).map((o) => <option key={o.id} value={o.id}>{formatDate(o.date, sc.ctx.locale, "weekday")} · {o.start}{o.status === "cancelled" ? " (cancelled)" : ""}</option>)}
              </select>
            </Field>
            <Field label="What happens" htmlFor="ex-status">
              <select id="ex-status" className={c.input} value={exStatus} onChange={(e) => setExStatus(e.target.value as "cancelled" | "scheduled")}>
                <option value="cancelled">Cancel the class</option><option value="scheduled">Held with a substitute</option>
              </select>
            </Field>
            {exStatus === "scheduled" ? (
              <Field label="Substitute trainer" htmlFor="ex-trainer">
                <select id="ex-trainer" className={c.input} value={exTrainer} onChange={(e) => setExTrainer(e.target.value)}><option value="">Choose a trainer</option>{list.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
              </Field>
            ) : null}
            <div><Button variant="primary" busy={exception.isPending} disabled={!pick} onClick={() => pick && exception.mutate({ date: pick.date, start: pick.start, status: exStatus, ...(exStatus === "scheduled" && exTrainer ? { trainerId: exTrainer } : {}) })}>Save exception</Button></div>
          </div>
        </Card>
      ) : null}
      <Card aria-label="Coming up">
        <CardHeader title="Coming up" subtitle="The next four weeks" />
        {upcoming.isPending ? <RowsSkeleton rows={3} /> : upcoming.error ? <Failure error={upcoming.error} title="Could not load upcoming classes" onRetry={() => void upcoming.refetch()} /> : upcoming.data.length === 0 ? <p className={`${c.note} ${c.pad}`}>Nothing scheduled in the next four weeks.</p> : (
          <ul className={c.list}>{upcoming.data.map((o) => <li key={o.id} className={c.li}><div><strong>{formatDate(o.date, sc.ctx.locale, "weekday")} · {o.start}</strong><span className={c.sub}>{nameOf(o.trainerId, o.trainerName)} · {o.seatsTaken ?? 0}/{o.capacity}</span></div>{o.status === "cancelled" ? <Badge>Cancelled</Badge> : null}</li>)}</ul>
        )}
      </Card>
      {editing ? <TemplateForm open template={t} onClose={() => setEditing(false)} /> : null}
    </div>
  );
}
